import Image from "common/components/Image";
import { useMatch } from "common/router/hooks";
import { StatusCode } from "common/fetch/types";

import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Error404Page from "client/common/components/errors/Error404Page";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import { Paths, urls } from "client/routes";

import { useStorefrontCollectionQuery } from "../hooks";
import CollectionContentBlocks from "./CollectionContentBlocks";
import CollectionRebateCardList from "./CollectionRebateCardList";
import CollectionResults from "./CollectionResults";
import { COLLECTION_PAGE_BY_KIND } from "../constants";
import { StorefrontCollection, StorefrontCollectionKind } from "../types";

import styles from "../styles/ProductCollectionPage.module.less";

type CollectionHeroProps = {
  collection: StorefrontCollection;
};

type ProductCollectionPageContentProps = {
  collection: StorefrontCollection;
  kind: StorefrontCollectionKind;
};

/**
 * The collection's name and copy, plus the banner when there is one. With a banner the copy sits
 * below the image, because manufacturer creative carries its own headline and overlaying ours
 * would collide.
 */
const CollectionHero = ({ collection }: CollectionHeroProps) => {
  const { headline, description } = collection.content;

  if (!collection.banner_image) {
    return (
      <div className={styles.textHero}>
        <h1 className={styles.textHeroName}>{collection.name}</h1>
        {headline && <p className={styles.textHeroHeadline}>{headline}</p>}
        {description && <p className={styles.textHeroDescription}>{description}</p>}
      </div>
    );
  }

  return (
    <>
      <Image className={styles.bannerImage} src={collection.banner_image} alt={collection.name} />
      <div className={styles.intro}>
        <h1 className={styles.title}>{collection.name}</h1>
        {headline && <p className={styles.headline}>{headline}</p>}
        {description && <p className={styles.description}>{description}</p>}
      </div>
    </>
  );
};

const ProductCollectionPageContent = ({ collection, kind }: ProductCollectionPageContentProps) => (
  <Page>
    <Body>
      <div className={styles.hero}>
        <CollectionHero collection={collection} />
      </div>
      {kind === StorefrontCollectionKind.Rebate && (
        <CollectionRebateCardList rebates={collection.rebate_highlights ?? []} />
      )}
      <CollectionResults
        collection={collection}
        listSource={COLLECTION_PAGE_BY_KIND[kind].listSource}
      />
    </Body>
    {/* Outside `Body`, matching ProductLinePage: a block like HERO_VISUAL is designed to run the
        full width and to place its own container, which a `Body` around it would clamp. */}
    <div className={styles.secondary}>
      <CollectionContentBlocks collection={collection} />
      {collection.isi && (
        <Container>
          <section className={styles.isi}>
            <h2 className={styles.isiTitle}>Important Safety Information</h2>
            <p className={styles.isiBody}>{collection.isi}</p>
          </section>
        </Container>
      )}
    </div>
  </Page>
);

// Marketing links are the only way onto these pages, so an unpublished or mistyped slug is a
// user following a printed URL. The search page's "Product Not Found!" copy would be wrong.
const CollectionNotFoundPage = () => (
  <Error404Page
    title="We can't find that page"
    description="This page may have moved, or it may not be available at your clinic."
    buttonText="Browse products"
    buttonLink={urls.products.search.to()}
  />
);

type ProductCollectionPageProps = {
  kind: StorefrontCollectionKind;
};

const ProductCollectionPage = ({ kind }: ProductCollectionPageProps) => {
  const match = useMatch<Paths["productCollections"]["detail"]>();
  const { slug } = match.params;
  const { data: collection, isLoading, error } = useStorefrontCollectionQuery(slug, kind);

  if (isLoading) {
    return <LoadingPage />;
  }
  if (error?.status === StatusCode.NotFound) {
    return <CollectionNotFoundPage />;
  }
  if (error || !collection) {
    return <FetchErrorPage error={error} entityName="page" />;
  }
  return <ProductCollectionPageContent key={collection.id} collection={collection} kind={kind} />;
};

export default ProductCollectionPage;
